import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useToast } from '../../modules/layout/components/ToastContext';
import { useNavigate } from 'react-router-dom';
import { update } from '../_request';

interface ApprovalToggleProps {
  profileId: string;
  currentState: 'Active' | 'Inactive';
  onChange: (newState: 'Active' | 'Inactive') => void; 
}

export default function ApprovalToggle({ profileId, currentState, onChange }: ApprovalToggleProps) {
  const { t } = useTranslation();
  const { showToast } = useToast();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [nextState, setNextState] = useState<'Active' | 'Inactive'>(currentState);

  const handleConfirm = async () => {
    setLoading(true);
    try {
      const url =
        nextState === 'Active'
          ? `/api/UserEvents/activateprofile?targetProfileId=${encodeURIComponent(profileId)}`
          : `/api/UserEvents/disableprofile?targetProfileId=${encodeURIComponent(profileId)}`;

      const result: any = await update(url, null);
      const respStatus = result?.status ?? result?.response?.status;

      if (respStatus === 200 || respStatus === 204 || respStatus === undefined) {
        showToast?.(
          nextState === 'Active'
            ? t('ProfileActivatedSuccessfully')
            : t('ProfileDisabledSuccessfully'),
          'success'
        );
        onChange(nextState);
      } else if (respStatus === 401) navigate('/auth/login');
      else if (respStatus === 403) navigate('/error/403');
      else if (respStatus === 404) showToast?.(t('ProfileNotFound'), 'error');
      else if (respStatus === 500) navigate('/error/500');
      else showToast?.(t('StatusCode', { code: String(respStatus) }), 'error');
    } catch (err: any) {
      console.error(err);
      showToast?.(t('ConnectionError', { error: String(err) }), 'error');
    } finally {
      setLoading(false);
      setOpen(false);
    }
  };

  const handleToggleClick = () => {
    setNextState(currentState === 'Active' ? 'Inactive' : 'Active');
    setOpen(true); 
  };

  return (
    <>
      <div className="form-check form-switch">
        <input
          className="form-check-input"
          type="checkbox"
          role="switch"
          checked={currentState === 'Active'}
          onChange={handleToggleClick}
          id={`profile-toggle-${profileId}`}
          disabled={loading}
        />
        <label className="form-check-label" htmlFor={`profile-toggle-${profileId}`}>
          {currentState === 'Active' ? t('Active') : t('Inactive')}
        </label>
      </div>

      {open && (
        <div className="gg-modal-overlay">
          <div className="gg-modal">
            <div className="gg-modal-header">
              <h5 className="mb-0">
                {nextState === 'Active' ? t('ActivateProfile') : t('DisableProfile')}
              </h5>
            </div>

            <div className="gg-modal-body">
              <p className="text-muted small mb-0">
                {nextState === 'Active' ? t('ActivatingRestores') : t('DisablingPrevents')}
              </p>
            </div>

            <div className="gg-modal-footer">
              <button className="gg-btn" onClick={() => setOpen(false)} disabled={loading}>
                {t('Cancel')}
              </button>

              <button 
                className="btn btn-secondary text-white"
                onClick={handleConfirm}
                disabled={loading}
              >
                {loading
                  ? nextState === 'Active'
                    ? t('Activating')
                    : t('Disabling')
                  : t('Confirm')}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
